import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Download, RefreshCw, PiggyBank } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';
import StatTile from './shared/StatTile';
import LedgerChart from './ledger/LedgerChart';
import LedgerAddForm from './ledger/LedgerAddForm';
import LedgerEntriesTable from './ledger/LedgerEntriesTable';

const INCOME_CATS = ['Service revenue', 'FFL transfer fee', 'Parts sold', 'Other income'];
const EXPENSE_CATS = ['Parts', 'Tools', 'Rent', 'Utilities', 'Software', 'Insurance', 'Shipping', 'Other expense'];

const todayISO = () => new Date().toISOString().slice(0, 10);
const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const AdminLedger = ({ onUnauthorized }) => {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [form, setForm] = useState({
    date: todayISO(),
    type: 'income',
    category: INCOME_CATS[0],
    amount: '',
    note: '',
    estimate_id: '',
    customer_email: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, est] = await Promise.all([
        axios.get(`${API}/api/ledger/summary?months=12`, { headers: authHeaders() }),
        axios.get(`${API}/api/ledger`, { headers: authHeaders() }),
        axios.get(`${API}/api/estimates`, { headers: authHeaders() }),
      ]);
      setSummary(s.data);
      setEntries(e.data);
      setEstimates(est.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error(`Failed to load: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  // Listen for cross-tab "pre-fill ledger" events from AdminEstimates (Log payment flow)
  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      setForm({
        date: todayISO(),
        type: 'income',
        category: INCOME_CATS[0],
        amount: d.amount ? String(d.amount) : '',
        note: d.note || '',
        estimate_id: d.estimate_id || '',
        customer_email: d.customer_email || '',
      });
      setTimeout(() => {
        document.querySelector("[data-testid='ledger-add-form']")?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    };
    window.addEventListener('ledger:prefill', handler);
    return () => window.removeEventListener('ledger:prefill', handler);
  }, []);

  const visibleEntries = useMemo(() => (
    filterType === 'all' ? entries : entries.filter((e) => e.type === filterType)
  ), [entries, filterType]);

  const onChangeType = (newType) => {
    setForm((f) => ({
      ...f,
      type: newType,
      category: newType === 'income' ? INCOME_CATS[0] : EXPENSE_CATS[0],
      estimate_id: newType === 'income' ? f.estimate_id : '',
    }));
  };

  const onSelectEstimate = (estId) => {
    if (!estId) {
      setForm((f) => ({ ...f, estimate_id: '', customer_email: '' }));
      return;
    }
    const est = estimates.find((e) => e.id === estId);
    if (!est) return;
    setForm((f) => ({
      ...f,
      estimate_id: estId,
      customer_email: est.customer_email || '',
      amount: f.amount || String(est.total || ''),
      note: f.note || `Payment for ${est.estimate_number} — ${est.customer_name}`,
    }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/ledger`, {
        date: form.date,
        type: form.type,
        category: form.category,
        amount,
        note: form.note.trim(),
        customer_email: form.customer_email.trim() || null,
        estimate_id: form.estimate_id || null,
      }, { headers: authHeaders() });
      toast.success(`Logged ${form.type === 'income' ? 'income' : 'expense'} of ${usd(amount)}`);
      setForm((f) => ({ ...f, amount: '', note: '', estimate_id: '', customer_email: '' }));
      load();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (entry) => {
    const ok = window.confirm(`Delete this ${entry.type} entry of ${usd(entry.amount)}?\n\nThis cannot be undone.`);
    if (!ok) return;
    const backup = entries;
    setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    try {
      await axios.delete(`${API}/api/ledger/${entry.id}`, { headers: authHeaders() });
      toast.success('Entry deleted');
      load();
    } catch (err) {
      setEntries(backup);
      toast.error(`Delete failed: ${apiErr(err)}`);
    }
  };

  const exportCsv = () => {
    if (entries.length === 0) {
      toast.error('No entries to export');
      return;
    }
    const header = ['Date', 'Type', 'Category', 'Amount', 'Note'];
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [...entries]
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
      .map((e) => [e.date, e.type, e.category, e.amount.toFixed(2), e.note].map(escape).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `taylord-ledger-${todayISO()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${entries.length} entries`);
  };

  if (loading && !summary) {
    return <p className="text-gray-400" data-testid="ledger-loading">Loading ledger…</p>;
  }

  const totals = summary?.totals || {};
  const timeline = summary?.timeline || [];
  const catList = form.type === 'income' ? INCOME_CATS : EXPENSE_CATS;

  return (
    <section data-testid="admin-ledger-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-green-400" /> Profits &amp; Spending
          </h2>
          <p className="text-sm text-gray-400">Track income, expenses, and monthly net. Numbers are for your eyes only.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="ledger-export" onClick={exportCsv} variant="outline" size="sm" className="border-slate-700 text-white hover:bg-slate-800">
            <Download className="w-3 h-3 mr-2" /> Export CSV
          </Button>
          <Button data-testid="ledger-refresh" onClick={load} variant="outline" size="sm" className="border-slate-700 text-white hover:bg-slate-800">
            <RefreshCw className="w-3 h-3 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile testid="ledger-stat-income-mtd" label="Income this month" value={usd(totals.income_mtd)} accent="green" />
        <StatTile testid="ledger-stat-expenses-mtd" label="Expenses this month" value={usd(totals.expenses_mtd)} accent="red" />
        <StatTile
          testid="ledger-stat-net-mtd"
          label="Net this month"
          value={usd(totals.net_mtd)}
          accent={(totals.net_mtd || 0) >= 0 ? 'green' : 'red'}
        />
        <StatTile testid="ledger-stat-net-ytd" label="Net YTD" value={usd(totals.net_ytd)} accent={(totals.net_ytd || 0) >= 0 ? 'green' : 'red'} sub={`${totals.entry_count || 0} entries logged`} />
      </div>

      {/* Chart + Add form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <LedgerChart timeline={timeline} />
        <LedgerAddForm
          form={form}
          setForm={setForm}
          catList={catList}
          estimates={estimates}
          onChangeType={onChangeType}
          onSelectEstimate={onSelectEstimate}
          onSubmit={submit}
          saving={saving}
        />
      </div>

      <LedgerEntriesTable
        visibleEntries={visibleEntries}
        entries={entries}
        estimates={estimates}
        filterType={filterType}
        setFilterType={setFilterType}
        onDelete={removeEntry}
      />
    </section>
  );
};

export default AdminLedger;
